import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AiAssistantService, AiAssistantContext } from '../../../service/services/misc/ai-assistant/ai-assistant.service';
import { AiAssistantConfig } from '../../../service/services/misc/ai-assistant/ai-assistant-config';
import { ThemeService } from '../../../service/services/misc/theme/theme.service';
import { Theme } from '../../../core/helpers/Const';

/**
 * The `<uassist-chat>` custom element's imperative API surface we rely on. The
 * methods only become functions once the widget's internal React tree mounts
 * (`widgetRef.current` set); until then they are absent.
 */
type WidgetElement = HTMLElement & {
    theme?: string;
    setContext?: (ctx: string) => unknown;
    sendWithContext?: (payload: { message: string }) => unknown;
    widgetRef?: { current?: unknown };
};

/**
 * Host for the embedded UAssist (`<uassist-chat>`) web component. Loads the widget
 * IIFE script once, renders the custom element with the configured attributes,
 * maps the app theme to the widget's dark/light theme (live), and injects the
 * context seed from AiAssistantService once the widget is ready.
 */
@Component({
    selector: 'app-ai-chat-panel',
    templateUrl: './ai-chat-panel.component.html',
    styleUrls: ['./ai-chat-panel.component.scss'],
    standalone: false
})
export class AiChatPanelComponent implements OnInit, AfterViewInit, OnDestroy {
    /** Widget attribute values (bound in the template). */
    readonly config = AiAssistantConfig;
    readonly title = 'Workspace Assistant';
    readonly placeholder = 'Ask a question, or investigate a resource…';
    readonly emptyStateSubtitle = 'Get answers on Unified Services and URM, or troubleshoot a resource.';

    /** Current widget theme ('dark' | 'light'). */
    theme: 'dark' | 'light' = 'dark';

    @ViewChild('widget') private _widget: ElementRef<WidgetElement>;

    private _subscriptions: Subscription[] = [];
    private _pendingContext: AiAssistantContext | null = null;

    constructor(
        private readonly _aiService: AiAssistantService,
        private readonly _themeService: ThemeService
    ) {}

    ngOnInit(): void {
        this.theme = this._mapTheme(this._themeService.selectedTheme);
        this._loadWidgetScript();

        this._subscriptions.push(
            this._themeService.themeSubject.subscribe((t) => {
                this.theme = this._mapTheme(t?.selectedTheme);
                const el = this._widget?.nativeElement as (HTMLElement & { theme?: string }) | undefined;
                if (el) {
                    el.setAttribute('theme', this.theme);
                    el.theme = this.theme;
                }
            }),
            this._aiService.context$.subscribe((ctx) => {
                this._pendingContext = ctx;
                if (ctx?.contextParts) {
                    this._applyContext(ctx);
                } else {
                    this._applyGeneralContext();
                }
            })
        );
    }

    ngAfterViewInit(): void {
        // Context may have been seeded before the element existed.
        if (this._pendingContext) {
            this._applyContext(this._pendingContext);
        } else {
            // No location/resource context: still seed the standing identity line.
            this._applyGeneralContext();
        }
        this._injectShadowStyles();
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach((s) => s.unsubscribe());
    }

    /** Map one of the three app themes to the widget's dark|light theme. */
    private _mapTheme(appTheme: string): 'dark' | 'light' {
        return appTheme === Theme.ATOM_LIGHT ? 'light' : 'dark';
    }

    /** Inject the context into the widget once it is defined + ready. */
    private _applyContext(ctx: AiAssistantContext | null): void {
        if (!ctx?.contextParts || !this._widget) {
            return;
        }
        const el = this._widget.nativeElement;
        const contextParts = `${AiAssistantService.GENERAL_CONTEXT}\n${ctx.contextParts}`;
        this._waitForWidgetReady(el)
            .then(() => Promise.resolve(el.setContext!(contextParts)))
            .then(() => {
                if (ctx.message && typeof el.sendWithContext === 'function') {
                    // Widget expects an object payload, not a bare string.
                    return Promise.resolve(el.sendWithContext({ message: ctx.message }));
                }
                return undefined;
            })
            .catch((): void => undefined);
    }

    /** Seed only the standing identity line (opened with no specific context). */
    private _applyGeneralContext(): void {
        if (!this._widget) {
            return;
        }
        const el = this._widget.nativeElement;
        this._waitForWidgetReady(el)
            .then(() => Promise.resolve(el.setContext!(this._aiService.buildGeneralContext())))
            .catch((): void => undefined);
    }

    /**
     * Resolve once the widget custom element is upgraded AND its internal React
     * handle is mounted (`isConnected` + `widgetRef.current`), so setContext /
     * sendWithContext actually take effect. Ported from UPC `_waitForWidgetReady`.
     */
    private _waitForWidgetReady(el: WidgetElement, timeoutMs = 10000): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const start = performance.now();
            const checkReady = (): void => {
                if (!el) {
                    reject(new Error('Widget not initialized'));
                    return;
                }
                if (typeof el.setContext !== 'function' || typeof el.sendWithContext !== 'function') {
                    if (performance.now() - start > timeoutMs) {
                        reject(new Error('Timed out waiting for uassist-chat widget'));
                        return;
                    }
                    setTimeout(checkReady, 100);
                    return;
                }
                const api = el.widgetRef && el.widgetRef.current;
                if (el.isConnected && api) {
                    resolve();
                    return;
                }
                if (performance.now() - start > timeoutMs) {
                    reject(new Error('Timed out waiting for uassist-chat widget'));
                    return;
                }
                setTimeout(checkReady, 100);
            };

            const defined = window.customElements?.whenDefined?.('uassist-chat');
            if (defined) {
                defined.then(() => {
                    el.addEventListener('uassist:ready', checkReady, { once: true });
                    checkReady();
                }).catch(reject);
            } else {
                checkReady();
            }
        });
    }

    /** Load the widget IIFE once (dedupe by marker attribute). */
    private _loadWidgetScript(): void {
        if (document.querySelector('script[data-uassist-widget]')) {
            return;
        }
        const script = document.createElement('script');
        script.src = AiAssistantConfig.widgetScriptUrl;
        script.defer = true;
        script.async = true;
        script.setAttribute('data-uassist-widget', 'true');
        document.head.appendChild(script);
    }

    /**
     * Inject an override stylesheet into the widget's (open) shadow root.
     *
     * The assistant response card hardcodes the Tailwind class `dark:bg-gray-900`
     * (no widget attribute or CSS var exposes it), so this is the only lever to
     * recolor it to the workspace surface. Brittle by nature — if the widget
     * renames that class in an update this silently stops matching; the proper
     * fix is an upstream background attribute/CSS var.
     */
    private _injectShadowStyles(): void {
        const el = this._widget?.nativeElement;
        if (!el) {
            return;
        }
        const apply = (): void => {
            const root = (el as HTMLElement & { shadowRoot?: ShadowRoot }).shadowRoot;
            if (!root || root.querySelector('style[data-atom-override]')) {
                return;
            }
            const style = document.createElement('style');
            style.setAttribute('data-atom-override', 'true');
            style.textContent = `.dark .bg-gray-900 { background-color: ${AiAssistantConfig.responseCardColor} !important; }`;
            root.appendChild(style);
        };

        const defined = window.customElements?.whenDefined?.('uassist-chat');
        if (defined) {
            defined.then(apply).catch((): void => undefined);
        } else {
            apply();
        }
    }
}
